import { useState, MouseEvent } from "react";
import { Button, Menu, MenuItem, Tooltip } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { addPortfolioItem } from "../../data/request";
import { DELTA_ACCENT } from "../colors";

type Portfolio = { portfolio_id: string; name: string };

// Extracted verbatim from CompanyDetailsPanels/CompanyActions.tsx's
// "Add to Portfolio" Button+Menu block so the same control can also be used
// per-row in the Dynamic Portfolios match table. `disabled`/`disabledTooltip`
// let a caller layer in its own gating reason (e.g. signed-out, not
// Enterprise) - this component additionally disables itself, with its own
// tooltip, whenever there are simply no portfolios to add to.
export const AddToPortfolioButton = (props: {
  portfolios: Portfolio[];
  companyNumber: string;
  companyName: string;
  disabled?: boolean;
  disabledTooltip?: string;
  onAdded?: () => void | Promise<void>;
}) => {
  const { portfolios, companyNumber, companyName, disabled, disabledTooltip, onAdded } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmation, setConfirmation] = useState<string>();

  const noPortfolios = portfolios.length === 0;
  const isDisabled = Boolean(disabled) || noPortfolios;
  const tooltipTitle = disabled
    ? disabledTooltip ?? ""
    : noPortfolios
    ? "Create a portfolio first on the My Portfolio page"
    : "";

  const handleAddToNamedPortfolio = async (portfolioId: string, portfolioName: string) => {
    setAnchorEl(null);
    await addPortfolioItem(portfolioId, companyNumber, companyName);
    setConfirmation(`Added to "${portfolioName}"`);
    await onAdded?.();
  };

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon />}
            disabled={isDisabled}
            onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
            style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
          >
            {confirmation ?? "Add to Portfolio"}
          </Button>
        </span>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {portfolios.map((p) => (
          <MenuItem key={p.portfolio_id} onClick={() => handleAddToNamedPortfolio(p.portfolio_id, p.name)}>
            {p.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AddToPortfolioButton;
